import { PriceBookStatus } from "@redotech/crdb-enums/inventory/price-book-status";
import { Currency } from "@redotech/money/iso4217/currencies";
import { accountIdFormat } from "@redotech/redo-account-id";
import {
  priceBookEntryIdFormat,
  priceBookIdFormat,
} from "@redotech/redo-marketplace-id";
import { productIdFormat } from "@redotech/redo-product-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const PriceBookStatusSchema = createEnum(PriceBookStatus);
export const CurrencySchema = createEnum(Currency, { enumName: "Currency" });
export const PriceBookCountriesSchema = z
  .array(z.string().regex(/^[A-Z]{2}$/))
  .min(1)
  .refine(
    (countries) => new Set(countries).size === countries.length,
    "Duplicate country",
  )
  .nullable();

const PriceBookRowSchema_ = z.object({
  id: priceBookIdFormat.schema,
  accountId: accountIdFormat.schema,
  name: z.string(),
  description: z.string(),
  status: PriceBookStatusSchema,
  currency: CurrencySchema.nullable(),
  countryCodes: PriceBookCountriesSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});
export const PriceBookRowSchema: z.ZodType<PriceBookRow> = PriceBookRowSchema_;
export interface PriceBookRow extends z.infer<typeof PriceBookRowSchema_> {}

const PriceBookEntrySchema_ = z.object({
  id: priceBookEntryIdFormat.schema,
  productId: productIdFormat.schema,
  productSku: z.string().nullable(),
  productTitle: z.string().nullable(),
  fixedPrice: z.bigint().nullable(),
  currency: CurrencySchema.nullable(),
});
export const PriceBookEntrySchema: z.ZodType<PriceBookEntry> =
  PriceBookEntrySchema_;
export interface PriceBookEntry extends z.infer<typeof PriceBookEntrySchema_> {}

const PriceBookTableRowSchema_ = PriceBookRowSchema_.extend({
  entryCount: z.number(),
});
export const PriceBookTableRowSchema: z.ZodType<PriceBookTableRow> =
  PriceBookTableRowSchema_;
export interface PriceBookTableRow extends z.infer<
  typeof PriceBookTableRowSchema_
> {}

const PriceBookDetailEntrySchema_ = z.object({
  id: priceBookEntryIdFormat.schema,
  productId: productIdFormat.schema,
  productSku: z.string().nullable(),
  productTitle: z.string().nullable(),
  fixedPrice: z.number().nullable(),
  currency: CurrencySchema.nullable(),
  kind: z.string(),
  imageUrl: z.string().nullable(),
});
export const PriceBookDetailEntrySchema: z.ZodType<PriceBookDetailEntry> =
  PriceBookDetailEntrySchema_;
export interface PriceBookDetailEntry extends z.infer<
  typeof PriceBookDetailEntrySchema_
> {}

const PriceBookDetailRowSchema_ = PriceBookRowSchema_.extend({
  entries: z.array(PriceBookDetailEntrySchema),
});
export const PriceBookDetailRowSchema: z.ZodType<PriceBookDetailRow> =
  PriceBookDetailRowSchema_;
export interface PriceBookDetailRow extends z.infer<
  typeof PriceBookDetailRowSchema_
> {}

export const priceBookStatusDisplayLabels: Record<PriceBookStatus, string> = {
  ACTIVE: "Active",
  DRAFT: "Draft",
  ARCHIVED: "Archived",
};
